import { SharedCard } from '@components';
import { useTenantVat, useView } from '@hooks';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Col, Row, Tag, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { IServiceDetailsCardProps } from './ServiceDetailsCard.props';
import { ServiceDetailsPresentation } from './ServiceDetailsCard.presentation';
import { ServiceDetailsMobile } from './ServiceDetailsCard.mobile';
import { ServiceDetailsTechnician } from './ServiceDetailsCard.technician';

const { Text } = Typography;

export const ServiceDetailsCard: React.FC<IServiceDetailsCardProps> = observer(
  ({ service, disabled = false, variant = 'selected' }) => {
    const { isMobile } = useView();
    const vat = useTenantVat();
    const { authenticationStore, serviceStore } = useStore();
    const userRole = authenticationStore.currentUser?.role;
    const shouldHideTechnicianButtons = userRole === 'Technician';

    const isAdditional = variant === 'additional';
    const partsMap = isAdditional ? serviceStore.additionalServiceParts : serviceStore.selectedServiceParts;
    const parts = partsMap.get(service.id) || [];

    const subTotal = useMemo(() => {
      const priceAmount = service.isFixed ? service.price : service.price * service.duration;
      const discountAmount = priceAmount * (service.discount / 100);
      return Utils.roundToTwoDecimals(priceAmount - discountAmount);
    }, [service]);

    const vatAmount = useMemo(() => {
      return service.isVat ? Utils.roundToTwoDecimals((subTotal * vat) / 100) : 0;
    }, [subTotal, vat, service]);

    // Total includes parts
    const partsTotal = useMemo(() => {
      const partsPrice = Utils.calculatePartsPrice(parts);
      const partsVat = parts.reduce(
        (acc, p) => acc + Utils.calculateVat({ ...p, duration: p.quantity, vat }),
        0
      );
      return partsPrice + partsVat;
    }, [parts, vat]);

    const total = Utils.roundToTwoDecimals(subTotal + vatAmount + partsTotal);

    const dispatchParts = isAdditional
      ? serviceStore.dispatchAdditionalServiceParts
      : serviceStore.dispatchSelectedServiceParts;

    const updateField = isAdditional
      ? serviceStore.updateAdditionalServiceField
      : serviceStore.updateServiceField;

    const sharedProps = {
      service,
      disabled,
      vat,
      subTotal,
      vatAmount,
      total,
      parts,
      onAddPart: () => dispatchParts({ type: 'ADD_SERVICE_PART', serviceId: service.id }),
      onRemovePart: (index: number) => dispatchParts({ type: 'REMOVE_SERVICE_PART', serviceId: service.id, index }),
      onUpdatePart: (index: number, values: any) => dispatchParts({ type: 'CHANGE_SERVICE_PART', serviceId: service.id, index, servicePart: values }),
      handlePriceUpdate: (id: number, price: number) => updateField(id, 'price', price),
      handleDurationUpdate: (id: number, duration: number) => updateField(id, 'duration', duration),
      handleDiscountUpdate: (id: number, discount: number) => updateField(id, 'discount', discount),
      handleNoteUpdate: (id: number, note: string) => updateField(id, 'note', note),
    };

    const accentCardStyle: React.CSSProperties = {
      borderColor: '#d4e4f7',
    };

    const renderContent = () => {
      if (shouldHideTechnicianButtons) {
        return <ServiceDetailsTechnician {...sharedProps} shouldHideTechnicianButtons={shouldHideTechnicianButtons} />;
      }
      if (isMobile) {
        return <ServiceDetailsMobile {...sharedProps} />;
      }
      return <ServiceDetailsPresentation {...sharedProps} shouldHideTechnicianButtons={shouldHideTechnicianButtons} />;
    };

    if (!isMobile && !shouldHideTechnicianButtons) {
      return <div style={{ padding: '8px 0' }}>{renderContent()}</div>;
    }

    return (
      <Row gutter={isMobile ? [4, 8] : [16, 16]} style={{ marginTop: isMobile ? 16 : 12, marginBottom: isMobile ? 16 : 0 }}>
        <Col span={24}>
          <SharedCard
            collapsible={isMobile && !shouldHideTechnicianButtons}
            defaultExpanded
            style={accentCardStyle}
            title={isMobile && !shouldHideTechnicianButtons ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <Text ellipsis={{ tooltip: service.name }} style={{ fontSize: 14, fontWeight: 600, flex: 1, minWidth: 0 }}>{service.name}</Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <Tag color={service.isFixed ? 'green' : 'blue'} style={{ margin: 0, fontSize: 14, padding: '2px 8px' }}>
                    {service.isFixed ? 'Fixed' : 'Labour'}
                  </Tag>
                  <Tag color="blue" style={{ fontSize: 14, fontWeight: 700, padding: '2px 8px', margin: 0 }}>
                    {Utils.formatCurrency(total)}
                  </Tag>
                </div>
              </div>
            ) : undefined}
          >
            {renderContent()}
          </SharedCard>
        </Col>
      </Row>
    );
  }
);
